import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Avatar,
  Checkbox,
  List,
  ListItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { BagIcon } from "src/@core/components/icons/BagIcon";
import EditIcon from "@mui/icons-material/Edit";
import toast from "react-hot-toast";
import UpgradePlanDialog from "src/@core/components/UpgradePlanDialog";
import { setShowSubscriptionPlanDialog } from "src/redux/slices/AuthSlice";

export default function CategorySelectDialog({
  isCatalog,
  handleClose,
  selectedCategories,
  setSelectedCategories,
  onAdd,
  onCreateCategory,
  onEditCategory,
}) {
  const [search, setSearch] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeDialogConfig, setUpgradeDialogConfig] = useState({
    featureType: "categories",
    currentQuantity: 0,
    maxQuantity: 0,
  });
  
  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  
  const user = useSelector((state) => state.auth.user);
  const selectedBusiness =
    user?.business?.filter(
      (business) => business._id === selectedBusinessId
    )[0] || null;

  // Delete categories mutation
  const deleteCategoriesMutation = useMutation(
    async (categoryIds) => {
      const promises = categoryIds.map((id) =>
        axiosInstance.delete(`${ApiEndPoints.MENU_CATEGORY.delete}/${id}`)
      );
      await Promise.all(promises);
      return categoryIds;
    },
    {
      onSuccess: (deletedIds) => {
        toast.success(
          `${deletedIds.length} categor${deletedIds.length === 1 ? "y" : "ies"} has been deleted`
        );
        // Refetch categories
        queryClient.invalidateQueries(["menuCategory", selectedBusinessId, isCatalog]);
        // Clear selected categories
        setSelectedCategories([]);
        setShowDeleteDialog(false);
      },
      onError: (error) => {
        console.error("Error deleting categories:", error);
        toast.error("Failed to delete categories. Please try again.");
      },
    }
  );

  const { data: menuCategory } = useQuery(
    ["menuCategory", selectedBusinessId, isCatalog], // Add isCatalog to query key
    async ({ signal }) => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.MENU_CATEGORY.get}/${selectedBusinessId}`,
        { 
          signal,
          params: {
            isCatalog: isCatalog,
          }
        }
      );
      return res.data.data;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 30 * 60 * 1000,
      enabled: !!selectedBusinessId,
    }
  );

  const categories = useMemo(
    () =>
      Array.isArray(menuCategory?.categories)
        ? menuCategory?.categories.map((cat) => ({
            id: cat._id,
            name: cat.name,
            image: cat.image,
            menuItemIds: cat?.menuItemIds || [],
          }))
        : [],
    [menuCategory]
  );

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;
    return categories.filter((cat) =>
      cat.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, categories]);

  // Check if object with id exists in selectedCategories
  const isSelected = (id) => selectedCategories.some((cat) => cat.id === id);

  // Toggle: add/remove the whole category object
  const handleToggle = (catObj) => {
    setSelectedCategories((prev) => {
      if (prev.some((c) => c.id === catObj.id)) {
        return prev.filter((c) => c.id !== catObj.id);
      } else {
        return [...prev, catObj];
      }
    });
  };

  const handleAddCategories = () => {
    handleClose();
    // If needed, you can call onAdd(selectedCategories) here
  };

  const handleDeleteSelected = () => {
    if (selectedCategories.length === 0) return;
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    const categoryIds = selectedCategories.map((cat) => cat.id);
    deleteCategoriesMutation.mutate(categoryIds);
  };

  return (
    <>
      <Box
        sx={{
          maxWidth: {
            xs: "100vw",
            sm: "95vw",
            md: 500,
            lg: 600,
            xl: 700,
          },
          mx: "auto",
          bgcolor: "#fff",
          borderRadius: {
            xs: "24px",
            sm: 3,
            md: 4,
          },
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          boxSizing: "border-box",
          "@media (max-width: 600px)": {
            minWidth: "100vw",
            maxWidth: "100vw",
            borderRadius: "24px",
            boxShadow: "0 3px 12px rgba(0,0,0,0.09)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            py: { xs: 2, sm: 2.5 },
            px: { xs: 2, sm: 3 },
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 18, sm: 20, md: 22, lg: 24 },
              color: "#000",
              letterSpacing: 0.5,
              textTransform: "lowercase",
            }}
          >
            select category
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                color: "#c09adf",
                background: "transparent",
                "&:hover": { color: "#8000c2", background: "#f3e7ff" },
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <TextField
            placeholder="search categories..."
            fullWidth
            size="small"
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              mb: { xs: 1.5, sm: 2 },
              flexShrink: 0,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#f8f9fa",
                fontSize: { xs: 14, sm: 16 },
                "&:hover": {
                  backgroundColor: "#f1f3f4",
                },
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              mb: { xs: 1.5, sm: 2 },
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              color: "#6c00e6",
              p: { xs: 0.5, sm: 1 },
              borderRadius: 2,
              transition: "all 0.2s",
              flexShrink: 0,
              "&:hover": {
                backgroundColor: "#f3e8ff",
                transform: "translateX(2px)",
              },
              fontSize: { xs: 13, sm: 14 },
              fontWeight: 600,
            }}
            onClick={() => {
              const features = selectedBusiness?.features || [];
              const categoriesfeature = features.find(
                (feature) => feature.featureKey === "categories"
              );
              if (categoriesfeature.isFree) {
                onCreateCategory();
              } else if (categoriesfeature.quantity > categories?.length) {
                onCreateCategory();
              } else {
                // Show upgrade dialog instead of redirecting
                setUpgradeDialogConfig({
                  featureType: "categories",
                  currentQuantity: categories?.length || 0,
                  maxQuantity: categoriesfeature.quantity,
                });
                setShowUpgradeDialog(true);
              }
            }}
          >
            <AddCircleOutlineIcon
              fontSize="small"
              sx={{ mr: 1, color: "#6c00e6" }}
            />
            create new category
          </Box>

          <Box
            sx={{
              mb: { xs: 2, sm: 3 },
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: { xs: 1, sm: 1.5 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 12, sm: 13 },
                  fontWeight: 600,
                  color: "#6b7280",
                  textTransform: "lowercase",
                  letterSpacing: 0.5,
                  flexShrink: 0,
                }}
              >
                Available Categories ({filteredCategories.length})
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {selectedCategories.length > 0 && (
                  <Typography
                    sx={{
                      fontSize: { xs: 11, sm: 12 },
                      color: "#dc2626",
                      fontWeight: 600,
                    }}
                  >
                    {selectedCategories.length} selected
                  </Typography>
                )}
                {selectedCategories.length > 0 && (
                  <IconButton
                    onClick={handleDeleteSelected}
                    sx={{
                      width: 30,
                      height: 30,
                      color: "#dc2626",
                      background: "#fef2f2",
                      "&:hover": {
                        background: "#fee2e2",
                      },
                    }}
                    disabled={deleteCategoriesMutation.isLoading}
                  >
                    {deleteCategoriesMutation.isLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DeleteIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    )}
                  </IconButton>
                )}
              </Box>
            </Box>
            <List
              sx={{
                flexGrow: 1,
                overflow: "auto",
                bgcolor: "#fafbfc",
                borderRadius: 2,
                p: { xs: 1, sm: 1.5 },
                minHeight: 0,
                maxHeight: { xs: "38vh", sm: "40vh", md: "45vh", lg: "52vh" },
                boxSizing: "border-box",
              }}
            >
              {filteredCategories.map((cat, i) => (
                <ListItem
                  key={cat.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 2,
                    bgcolor: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    mb: { xs: 2, sm: 2 },
                    width: "100%",
                    overflow: "hidden",
                    gap: 2,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#222",
                      fontWeight: 500,
                      fontSize: 15,
                    }}
                  >
                    {i + 1}
                  </Typography>
                  <Checkbox
                    checked={isSelected(cat.id)}
                    onChange={(event) => {
                      event.stopPropagation();
                      handleToggle(cat);
                    }}
                    size="medium"
                    sx={{
                      color: "#6c00e6",
                      "&.Mui-checked": { color: "#6c00e6" },
                    }}
                  />
                  <Box sx={{ position: "relative", mr: 1 }}>
                    <Avatar
                      variant="rounded"
                      src={cat.image || undefined}
                      alt={cat.name}
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        bgcolor: "#f3f4f6",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: -6,
                        right: -8,
                        background: "#fff",
                        borderRadius: "50%",
                        p: "1px",
                      }}
                      onClick={() => {
                        console.log("cat---", cat);
                        onEditCategory(null);
                        onEditCategory(cat);
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "#8000c2",
                          width: 22,
                          height: 22,
                          border: "2px solid #fff",
                        }}
                      >
                        <EditIcon sx={{ fontSize: 14, color: "#fff" }} />
                      </Avatar>
                    </Box>
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#222",
                      fontSize: 16,
                      minWidth: 120,
                    }}
                  >
                    {cat.name}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: "auto",
                      gap: 1,
                    }}
                  >
                    <BagIcon />
                    <Box
                      sx={{
                        ml: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#8000c2",
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        {cat?.menuItemIds?.length || 0} items
                      </Typography>
                      <Typography
                        sx={{
                          color: "#bdbdbd",
                          fontWeight: 500,
                          fontSize: 14,
                        }}
                      >
                        add items
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              ))}

              {!filteredCategories.length && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: { xs: 3, sm: 4 },
                    color: "#9ca3af",
                  }}
                >
                  <Typography sx={{ fontSize: { xs: 13, sm: 14 }, mb: 1 }}>
                    No categories found
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 11, sm: 12 } }}>
                    Try adjusting your search or create a new category
                  </Typography>
                </Box>
              )}
            </List>
          </Box>

          <Button
            variant="contained"
            fullWidth
            disabled={selectedCategories.length === 0}
            sx={{
              bgcolor: "#6c00e6",
              color: "#fff",
              borderRadius: 2,
              fontWeight: 700,
              fontSize: { xs: 14, sm: 16 },
              textTransform: "lowercase",
              boxShadow: "0 4px 12px rgba(108, 0, 230, 0.3)",
              flexShrink: 0,
              "&:hover": {
                bgcolor: "#5a00c2",
                boxShadow: "0 6px 16px rgba(108, 0, 230, 0.4)",
              },
              "&:disabled": {
                bgcolor: "#e5e7eb",
                color: "#9ca3af",
                boxShadow: "none",
              },
              height: { xs: 44, sm: 48 },
              letterSpacing: 0.5,
            }}
            onClick={handleAddCategories}
          >
            add{" "}
            {selectedCategories.length > 0
              ? `${selectedCategories.length} `
              : ""}
            categor
            {selectedCategories.length === 1 ? "y" : "ies"}
          </Button>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: {
              xs: "100vw",
              sm: "95vw",
              md: 500,
              lg: 600,
              xl: 700,
            },
            mx: "auto",
            bgcolor: "#fff",
            borderRadius: {
              xs: "24px",
              sm: 3,
              md: 4,
            },
            overflow: "hidden",
            boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
            "@media (max-width: 600px)": {
              minWidth: "100vw",
              maxWidth: "100vw",
              borderRadius: "24px",
              boxShadow: "0 3px 12px rgba(0,0,0,0.09)",
            },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Delete Categories</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete {selectedCategories.length} categor
            {selectedCategories.length === 1 ? "y" : "ies"}?
          </Typography>
          <Typography sx={{ color: "#dc2626", fontSize: 14 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setShowDeleteDialog(false)}
            sx={{ color: "#6b7280" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            sx={{
              bgcolor: "#dc2626",
              "&:hover": { bgcolor: "#b91c1c" },
            }}
            disabled={deleteCategoriesMutation.isLoading}
          >
            {deleteCategoriesMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upgrade Plan Dialog */}
      <UpgradePlanDialog
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        selectedBusiness={selectedBusiness}
        featureType={upgradeDialogConfig.featureType}
        currentQuantity={upgradeDialogConfig.currentQuantity}
        maxQuantity={upgradeDialogConfig.maxQuantity}
        onUpgrade={() => {
          dispatch(
            setShowSubscriptionPlanDialog({
              show: true,
              businessId: selectedBusinessId,
              currentPlan: selectedBusiness?.planName,
            })
          );
        }}
      />
    </>
  );
}
